import { Dispatch, SetStateAction, useCallback, useMemo, useState } from "react";
import {
  ReturnForMethod,
  useAuth,
  usePermissionQuery,
  useTellescopeInfiniteQuery,
  useTellescopeMutation,
  useTellescopeQuery,
  useUsersMap,
} from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";
import { addMinutes } from "date-fns/addMinutes";
import { startOfDay } from "date-fns/startOfDay";
import { formatInTimeZone } from "date-fns-tz";
import { createIdMap, formatName } from "@tellescope/utils";
import { useDateFnsLocale } from "@tellescope/ui";

type UpdateEventMutation = ReturnType<typeof useTellescopeMutation<"calendar_events", "updateOne">>;

export interface UseEventDetailsDialogContentsOptions {
  eventId: string;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onCopyId?: () => void;
  onDeleteSuccess?: () => void;
  timezone?: string;
}

type DeleteEventMutation = ReturnType<typeof useTellescopeMutation<"calendar_events", "deleteOne">>;

export interface UseEventDetailsDialogContentsReturn {
  actionsAreOpen: boolean;
  setActionsAreOpen: Dispatch<SetStateAction<boolean>>;
  deleteWarningIsOpen: boolean;
  setDeleteWarningIsOpen: Dispatch<SetStateAction<boolean>>;
  event: ReturnForMethod<"calendar_events", "getOne"> | undefined;
  title: string | undefined;
  dateString: string;
  locationStrings: string[];
  userNames: string[];
  patients: { name: string; id: string }[];
  descriptionText: string | undefined;
  isCompleted: boolean;
  isNoShow: boolean;
  isCancelled: boolean;
  hasBadges: boolean;
  statusRowCount: number;
  toggleCompleted: () => ReturnType<UpdateEventMutation["mutateAsync"]>;
  toggleNoShow: () => ReturnType<UpdateEventMutation["mutateAsync"]>;
  toggleCancelled: () => ReturnType<UpdateEventMutation["mutateAsync"]>;
  copyEventId: () => void;
  deleteEvent: () => Promise<void>;
  updateEventMutation: UpdateEventMutation;
  deleteEventMutation: DeleteEventMutation;
  lastStatusUpdateName: string | undefined;
  completedByName: string | undefined;
  canEdit: boolean;
  canDelete: boolean;
}

export const useEventDetailsDialogContents = ({
  eventId,
  onCopyId,
  onDeleteSuccess,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
}: UseEventDetailsDialogContentsOptions): UseEventDetailsDialogContentsReturn => {
  const { t } = useTranslation("calendar");
  const { currentUser } = useAuth();
  const [actionsAreOpen, setActionsAreOpen] = useState(false);
  const [deleteWarningIsOpen, setDeleteWarningIsOpen] = useState(false);

  const eventQuery = useTellescopeQuery({
    model: "calendar_events",
    method: "getOne",
    request: eventId,
    enabled: !!eventId,
  });
  const { update: updatePermission, delete: deletePermission } = usePermissionQuery({
    model: "calendar_events",
    actions: ["update", "delete"],
    record: eventQuery.data,
  });
  const canEdit = updatePermission.access;
  const canDelete = deletePermission.access;

  const eventStartDate = useMemo(
    () => (eventQuery.data ? new Date(eventQuery.data?.startTimeInMS) : undefined),
    [eventQuery.data]
  );
  const eventEndDate =
    eventStartDate && eventQuery.data ? addMinutes(eventStartDate, eventQuery.data.durationInMinutes) : undefined;
  const isSameDay =
    eventStartDate && eventEndDate ? startOfDay(eventStartDate).getTime() === startOfDay(eventEndDate).getTime() : true;

  const updateEventMutation = useTellescopeMutation({
    model: "calendar_events",
    method: "updateOne",
  });
  const deleteEventMutation = useTellescopeMutation({
    model: "calendar_events",
    method: "deleteOne",
  });

  const isCompleted = !!eventQuery.data?.completedAt;
  const isNoShow = !!eventQuery.data?.noShowedAt;
  const isCancelled = !!eventQuery.data?.cancelledAt;

  const statusEnduserId = useMemo(() => {
    if (
      eventQuery.data?.statusChangeSource?.source !== "enduser" ||
      eventQuery.data?.statusChangeSource?.byEnduserExternal
    ) {
      return undefined;
    }
    return eventQuery.data?.statusChangeSource.identifier;
  }, [
    eventQuery.data?.statusChangeSource?.byEnduserExternal,
    eventQuery.data?.statusChangeSource?.identifier,
    eventQuery.data?.statusChangeSource?.source,
  ]);
  const statusEnduserExternalId = useMemo(() => {
    if (
      eventQuery.data?.statusChangeSource?.source !== "enduser" ||
      !eventQuery.data?.statusChangeSource?.byEnduserExternal
    ) {
      return undefined;
    }
    return eventQuery.data?.statusChangeSource?.identifier;
  }, [
    eventQuery.data?.statusChangeSource?.byEnduserExternal,
    eventQuery.data?.statusChangeSource?.identifier,
    eventQuery.data?.statusChangeSource?.source,
  ]);
  const statusEnduserQuery = useTellescopeQuery({
    model: "endusers",
    method: "getOne",
    request: statusEnduserId ?? "",
    enabled: !!statusEnduserId,
  });
  const statusEnduserExternalQuery = useTellescopeInfiniteQuery({
    model: "endusers",
    method: "getSome",
    request: {
      mdbFilter: {
        externalId: statusEnduserExternalId,
      },
    },
    enabled: !!statusEnduserExternalId,
    select: (data) => data.pages.flat().at(0),
  });

  const toggleCompleted = useCallback(() => {
    setActionsAreOpen(false);
    return updateEventMutation.mutateAsync({
      id: eventId,
      completedAt: isCompleted ? "" : new Date(),
      completedBy: isCompleted ? "" : currentUser.id,
      statusChangeSource: {
        identifier: currentUser.id,
        source: "user",
      },
      _options: {
        replaceObjectFields: true,
      },
    });
  }, [currentUser.id, eventId, isCompleted, updateEventMutation]);
  const toggleNoShow = useCallback(() => {
    setActionsAreOpen(false);
    return updateEventMutation.mutateAsync({
      id: eventId,
      noShowedAt: isNoShow ? "" : new Date(),
      statusChangeSource: {
        identifier: currentUser.id,
        source: "user",
      },
      _options: {
        replaceObjectFields: true,
      },
    });
  }, [currentUser.id, eventId, isNoShow, updateEventMutation]);
  const toggleCancelled = useCallback(() => {
    setActionsAreOpen(false);
    return updateEventMutation.mutateAsync({
      id: eventId,
      cancelledAt: isCancelled ? "" : new Date(),
      statusChangeSource: {
        identifier: currentUser.id,
        source: "user",
      },
      _options: {
        replaceObjectFields: true,
      },
    });
  }, [currentUser.id, eventId, isCancelled, updateEventMutation]);

  const copyEventId = useCallback(() => {
    navigator.clipboard.writeText(eventId);
    onCopyId?.();
    setActionsAreOpen(false);
  }, [eventId, onCopyId]);

  const deleteEvent = useCallback(async () => {
    await deleteEventMutation.mutateAsync(eventId);
    setDeleteWarningIsOpen(false);
    setActionsAreOpen(false);
    onDeleteSuccess?.();
  }, [deleteEventMutation, eventId, onDeleteSuccess]);

  const locale = useDateFnsLocale();
  const dateString = useMemo(() => {
    return `${
      eventStartDate ? formatInTimeZone(eventStartDate, timezone, "MMM d, yyyy", { locale }) : t("Loading...")
    } · ${eventStartDate ? formatInTimeZone(eventStartDate, timezone, "h:mm a", { locale }) : t("Loading...")} – ${
      eventEndDate
        ? formatInTimeZone(eventEndDate, timezone, isSameDay ? "h:mm a zzz" : "MMM d, yyyy h:mm a zzz", {
            locale,
          })
        : t("Loading...")
    }`;
  }, [eventEndDate, eventStartDate, isSameDay, locale, t, timezone]);
  const userMap = useUsersMap();
  const statusLastUpdatedBy = useMemo(() => {
    if (eventQuery.data?.statusChangeSource?.byEnduserExternal) {
      return statusEnduserExternalQuery.data;
    } else if (eventQuery.data?.statusChangeSource?.source === "user") {
      return userMap[eventQuery.data?.statusChangeSource.identifier];
    } else if (eventQuery.data?.statusChangeSource?.source === "enduser") {
      return statusEnduserQuery.data;
    }

    return undefined;
  }, [
    eventQuery.data?.statusChangeSource?.byEnduserExternal,
    eventQuery.data?.statusChangeSource?.identifier,
    eventQuery.data?.statusChangeSource?.source,
    statusEnduserExternalQuery.data,
    statusEnduserQuery.data,
    userMap,
  ]);
  const lastStatusUpdateName = statusLastUpdatedBy ? formatName(statusLastUpdatedBy) : undefined;

  const badgeCount = [isCompleted, isCancelled, isNoShow].filter(Boolean).length;
  const statusRowCount = badgeCount + (lastStatusUpdateName ? 1 : 0);
  const hasBadges = !!badgeCount;

  const allLocationIds = Array.from(
    new Set([eventQuery.data?.locationId ?? "", ...(eventQuery.data?.locationIds ?? [])].filter(Boolean))
  );
  const fetchedLocationsQuery = useTellescopeQuery({
    model: "appointment_locations",
    method: "getByIds",
    request: {
      ids: allLocationIds,
    },
    enabled: !!allLocationIds.length,
  });
  const locationStrings = useMemo<string[]>(() => {
    const result: string[] = [];
    if (eventQuery.data?.location) {
      result.push(eventQuery.data.location);
    }
    if (fetchedLocationsQuery.data?.matches.length) {
      fetchedLocationsQuery.data.matches.forEach((location) => {
        result.push(location.title);
      });
    }

    return result;
  }, [eventQuery.data?.location, fetchedLocationsQuery.data?.matches]);

  const splitAttendeeIds = useMemo(() => {
    const sets = eventQuery.data?.attendees.reduce<{ users: Set<string>; patients: Set<string> }>(
      (acc, curr) => {
        if (curr.type === "user") {
          acc.users.add(curr.id);
        } else {
          acc.patients.add(curr.id);
        }

        return acc;
      },
      { users: new Set<string>(), patients: new Set<string>() }
    );

    return {
      users: Array.from(sets?.users ?? []),
      patients: Array.from(sets?.patients ?? []),
    };
  }, [eventQuery.data?.attendees]);
  const endusersQuery = useTellescopeQuery({
    model: "endusers",
    method: "getByIds",
    request: { ids: splitAttendeeIds.patients },
    enabled: !!splitAttendeeIds.patients.length,
    select: (data) => createIdMap(data.matches),
  });
  const completedByEnduserQuery = useTellescopeQuery({
    model: "endusers",
    method: "getOne",
    request: eventQuery.data?.completedBy ?? "",
    enabled: !userMap[eventQuery.data?.completedBy ?? ""] && !!eventQuery.data?.completedBy,
  });
  const userNames = useMemo(
    () =>
      splitAttendeeIds.users.reduce<string[]>((acc, id) => {
        const user = userMap[id];
        if (!user) return acc;

        acc.push(formatName(user));
        return acc;
      }, []),
    [splitAttendeeIds.users, userMap]
  );
  const patients = useMemo(
    () =>
      splitAttendeeIds.patients.reduce<{ name: string; id: string }[]>((acc, id) => {
        const enduser = endusersQuery.data?.[id];
        if (!enduser) return acc;

        acc.push({ name: formatName(enduser), id });
        return acc;
      }, []),
    [endusersQuery.data, splitAttendeeIds.patients]
  );
  const descriptionText = useMemo(
    () => eventQuery.data?.displayDescription || eventQuery.data?.description,
    [eventQuery.data?.description, eventQuery.data?.displayDescription]
  );
  const completedByName = useMemo(() => {
    const actor = userMap[eventQuery.data?.completedBy ?? ""] ?? completedByEnduserQuery.data;
    if (!actor) return undefined;
    return formatName(actor);
  }, [completedByEnduserQuery.data, eventQuery.data?.completedBy, userMap]);

  return useMemo(
    () => ({
      actionsAreOpen,
      setActionsAreOpen,
      deleteWarningIsOpen,
      setDeleteWarningIsOpen,
      event: eventQuery.data,
      title: eventQuery.data?.displayTitle || eventQuery.data?.title,
      dateString,
      locationStrings,
      userNames,
      patients,
      descriptionText,
      isCompleted,
      isNoShow,
      isCancelled,
      hasBadges,
      statusRowCount,
      toggleCompleted,
      toggleNoShow,
      toggleCancelled,
      copyEventId,
      deleteEvent,
      updateEventMutation,
      deleteEventMutation,
      lastStatusUpdateName,
      completedByName,
      canEdit,
      canDelete,
    }),
    [
      actionsAreOpen,
      deleteWarningIsOpen,
      eventQuery.data,
      dateString,
      locationStrings,
      userNames,
      patients,
      descriptionText,
      isCompleted,
      isNoShow,
      isCancelled,
      hasBadges,
      statusRowCount,
      toggleCompleted,
      toggleNoShow,
      toggleCancelled,
      copyEventId,
      deleteEvent,
      updateEventMutation,
      deleteEventMutation,
      lastStatusUpdateName,
      completedByName,
      canEdit,
      canDelete,
    ]
  );
};
